/**
 * PeopleTable Selectors
 */
import { createSelectorCreator, defaultMemoize } from 'reselect';
import isEqual from 'lodash.isequal';
import search from 'javascript-array-search';

// Selectors
import { selectPeople } from '../../../../../../services/people/selectors';

// Workspaces
import {
    makeSelectQueryStringValue,
    makeSelectQueryStringNumberValue
} from '../../../../../../workspaces/react-router-selectors';

// Utils
import orderBy from '../../../../../../utils/custom-orderBy';
import { selectDataGroupedByGroupId } from '../../../../../../services/groupPeople/selectors';

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

const selectData = createDeepEqualSelector(selectPeople, peopleState =>
    Object.values(peopleState)
);

const selectPeopleFiltered = createDeepEqualSelector(
    [
        selectData,
        selectDataGroupedByGroupId,
        makeSelectQueryStringValue('groups'),
        makeSelectQueryStringValue('roles')
    ],
    (
        dataState,
        groupsByIdState,
        groupsFilterState = [],
        rolesFilterState = []
    ) => {
        if (!groupsFilterState.length && !rolesFilterState.length) {
            return dataState;
        }

        const filteredPeopleId = [];

        // Builds an array of people id filtered by Groups Filter
        groupsFilterState.forEach(id => {
            const people = groupsByIdState[id];

            if (people && people.length) {
                people.forEach(({ personId }) =>
                    filteredPeopleId.push(personId)
                );
            }
        });

        return dataState.filter(person => {
            let showPerson = true;

            if (rolesFilterState.length) {
                showPerson = rolesFilterState.some(id => {
                    return person.roleIds.includes(Number(id));
                });
            }

            if (groupsFilterState.length && showPerson) {
                showPerson = filteredPeopleId.includes(person.id);
            }

            return showPerson;
        });
    }
);

const selectFilteredData = createDeepEqualSelector(
    [makeSelectQueryStringValue('search'), selectPeopleFiltered],
    (searchState, selectPeopleFilteredState) => {
        return search(selectPeopleFilteredState, searchState, [
            'firstName',
            'lastName',
            'datacomId',
            'email'
        ]);
    }
);

const selectDataLength = createDeepEqualSelector(
    selectFilteredData,
    filteredDataState => filteredDataState.length
);

const selectOrderedData = createDeepEqualSelector(
    [selectFilteredData, makeSelectQueryStringValue('sort')],
    (filteredDataState, sortState = 'firstName_ascending') => {
        const attributes = sortState.split('_');
        const propertyName = attributes[0];
        const order = attributes[1] !== 'descending' ? 'asc' : 'desc';
        return orderBy(filteredDataState, propertyName, order);
    }
);

const selectPaginatedData = createDeepEqualSelector(
    [
        selectOrderedData,
        makeSelectQueryStringNumberValue('page'),
        makeSelectQueryStringNumberValue('count')
    ],
    (orderedDataState, pageState = 1, countState = 25) => {
        const begin = (pageState - 1) * countState;
        const end = pageState * countState;
        return orderedDataState.slice(begin, end);
    }
);

const selectIds = createDeepEqualSelector(
    selectPaginatedData,
    paginatedDataState =>
        paginatedDataState && paginatedDataState.map(value => value.id)
);

export {
    selectData,
    selectDataLength,
    selectFilteredData,
    selectOrderedData,
    selectIds,
    selectPeopleFiltered
};
